import React, { useEffect, useState } from "react";
import useOutsideClick from "hooks/click-outside";
import { QRCodeCanvas } from "qrcode.react";
import useStore from "hooks/useStore";
import { generateQRData } from "utils/helpers/qrUtils";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import useMinor from "containers/minor/useHooks";

interface TicketPopupProps {
  isOpen: boolean;
  onClose: () => void;
  eventData?: any;
  selectedSubscriber?: any;
}

const PassModal: React.FC<TicketPopupProps> = ({
  isOpen,
  onClose,
  eventData,
  selectedSubscriber,
}) => {
  const modalRef = useOutsideClick(() => {
    onClose();
  });


  const { getUserProfilePic, getUser } = useStore();
  const userData:any = getUser();
  const attendeeId = userData?.profiles?.child?.attendeeId;

  // State to track event time status
  const [isEventActive, setIsEventActive] = useState(false);
  const [hasEventEnded, setHasEventEnded] = useState(false);
  const [currentActiveSession, setCurrentActiveSession] = useState<any>(null);

  // Function to get the currently active session (time is between start and end)
  const getCurrentActiveSession = () => {
    if (!eventData?.workshopSessions || eventData.workshopSessions.length === 0) {
      return null;
    }

    const now = new Date();
    const sessions = eventData.workshopSessions || [];

    // Sort sessions by startDateTime (earliest first)
    const sortedSessions = [...sessions].sort((a, b) => {
      const timeA = new Date(a.startDateTime).getTime();
      const timeB = new Date(b.startDateTime).getTime();
      return timeA - timeB;
    });

    // First, try to find a session that is currently active (now is between start and end)
    // Check in reverse order to prioritize later sessions if multiple are active (shouldn't happen, but just in case)
    for (let i = sortedSessions.length - 1; i >= 0; i--) {
      const session = sortedSessions[i];
      const startTime = new Date(session.startDateTime);
      const endTime = new Date(session.endDateTime);
      
      // Check if current time is within this session's time range
      if (now >= startTime && now <= endTime) {
        return session; // Found currently active session
      }
    }

    // If no active session, find the next upcoming session (not yet started)
    for (const session of sortedSessions) {
      const startTime = new Date(session.startDateTime);
      if (now < startTime) {
        return session; // Found next upcoming session
      }
    }

    // If all sessions have passed, return null to show "event ended" message
    return null;
  };

  // Function to check overall event status
  const checkEventTimeStatus = () => {
    if (!eventData?.workshopSessions || eventData.workshopSessions.length === 0) {
      setIsEventActive(false);
      setHasEventEnded(false);
      setCurrentActiveSession(null);
      return;
    }

    const now = new Date();
    const sessions = eventData.workshopSessions || [];

    // Sort sessions by startDateTime to get the earliest session
    const sortedByStart = [...sessions].sort((a, b) => 
      new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
    );

    // Sort sessions by endDateTime to get the latest session
    const sortedByEnd = [...sessions].sort((a, b) => 
      new Date(b.endDateTime).getTime() - new Date(a.endDateTime).getTime()
    );

    const firstSession = sortedByStart.length > 0 ? sortedByStart[0] : null;
    const lastSession = sortedByEnd.length > 0 ? sortedByEnd[0] : null;

    if (!firstSession || !lastSession) {
      setIsEventActive(false);
      setHasEventEnded(false);
      setCurrentActiveSession(null);
      return;
    }

    const firstStart = new Date(firstSession.startDateTime);
    const lastEnd = new Date(lastSession.endDateTime);

    // Check if event has started
    const eventStarted = now >= firstStart;
    // Check if event has ended (all sessions have ended)
    const eventEnded = now >= lastEnd;
    
    // Check if there's a currently active session
    const activeSession = getCurrentActiveSession();
    const eventActive = activeSession !== null && 
      now >= new Date(activeSession.startDateTime) && 
      now <= new Date(activeSession.endDateTime);

    setIsEventActive(eventActive);
    setHasEventEnded(eventEnded);
    setCurrentActiveSession(activeSession);
  };

  // Helper functions to format time and date
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // Check event time status on mount and when eventData changes
  useEffect(() => {
    if (isOpen && eventData) {
      // Initial check
      checkEventTimeStatus();
      
      // Set up interval to check every 30 seconds for more accurate updates
      const interval = setInterval(() => {
        checkEventTimeStatus();
      }, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    } else {
      // Reset state when modal closes
      setIsEventActive(false);
      setHasEventEnded(false);
      setCurrentActiveSession(null);
    }
  }, [isOpen, eventData]);

  // Use selectedSubscriber if available, otherwise fall back to first attendee from eventSubscriptions
  const participant = selectedSubscriber;

  // Generate QR code data for the specific attendee
  const qrData = participant
    ? generateQRData(participant, eventData, attendeeId)
    : "learnlocal.app";

  // Calculate current active session directly (ensures we always have the latest)
  // This is a fallback in case state hasn't updated yet
  const activeSessionForDisplay = currentActiveSession || getCurrentActiveSession();

  if (!isOpen) return null;
  return (
    <div
      className={`fixed z-50 inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className={`relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%]  sm:w-2/3 lg:w-[568px] transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center"
        >
          ✕
        </button>

        {/* User Info */}
        <div className="flex flex-col items-center">
          <img
            src={getUserProfilePic()}
            alt="Profile"
            className="w-20 h-20 object-cover rounded-full mb-2"
          />
          <h2 className="text-base text-blackie font-semibold capitalize">
            {eventData?.eventSubscriptions[0]?.firstName}{" "}
            {eventData?.eventSubscriptions[0]?.lastName}
          </h2>
        </div>

        {/* Ticket Details */}
        <div className="flex mt-4 bg-passBg border border-borderColor rounded-lg overflow-hidden">
          {/* Left Section - QR Code */}
          {!hasEventEnded && (
            <div className="flex flex-col items-center justify-center p-4 border-r-2 border-dashed border-dashedBorder w-[180px]">
              <QRCodeCanvas value={qrData} size={100} />
              <p className="text-xs text-black mt-2">SCAN QR CODE</p>
            </div>
          )}

          {/* Center Section - Event Details */}
          <div className="p-4 text-center">
            <h3 className="text-base  font-medium text-blackie">
              {/* {minorAddress} */}
            </h3>
            {hasEventEnded ? (
              // Show message when event has ended
              <div className="mt-3 flex flex-col items-center justify-center gap-2 py-4">
                <div className="text-lg font-semibold text-gray-600">
                  Event Completed
                </div>
                <div className="text-sm text-gray-500">
                  This event has ended. Thank you for participating!
                </div>
              </div>
            ) : activeSessionForDisplay ? (
              // Show event time for currently active or next upcoming session
              <div className="mt-3 flex flex-wrap  items-center gap-2 gap-x-3 text-sm">
                <div className="flex items-start flex-col gap-0">
                  <span className="text-xs  text-blackie">Start Time</span>
                  <span className="text-AttendeeBlue text-base">
                    {activeSessionForDisplay.startDateTime 
                      ? formatTime(activeSessionForDisplay.startDateTime)
                      : "-"}
                  </span>
                </div>
                <div className="flex items-start flex-col gap-0">
                  <span className="text-xs text-blackie">End Time</span>
                  <span className="text-AttendeeBlue text-base">
                    {activeSessionForDisplay.endDateTime
                      ? formatTime(activeSessionForDisplay.endDateTime)
                      : "-"}
                  </span>
                </div>
                <div className="flex items-start flex-col gap-0">
                  <span className="text-xs text-blackie">Price</span>
                  <span className="text-AttendeeBlue text-base">
                    {eventData?.entryFee === 0 || eventData?.entryFee === "0"
                      ? "Free"
                      : `$${parseFloat(eventData?.entryFee).toFixed(2)}`}
                  </span>
                </div>

                <div className="flex items-start flex-col gap-0">
                  <span className="text-xs text-blackie">Date</span>
                  <span className="text-AttendeeBlue text-base">
                    {activeSessionForDisplay.startDateTime
                      ? formatDate(activeSessionForDisplay.startDateTime)
                      : "-"}
                  </span>
                </div>
                <div className="flex items-start flex-col gap-0">
                  <span className="text-xs text-blackie">Address/Location</span>
                  <span className="text-AttendeeBlue text-left text-base">
                    {eventData?.location}
                  </span>
                </div>
              </div>
            ) : (
              // Fallback if no session found
              <div className="mt-3 flex flex-col items-center justify-center gap-2 py-4">
                <div className="text-sm text-gray-500">
                  No session information available
                </div>
              </div>
            )}
          </div>

          {/* Right Section - Extra Details */}
          <div className={`flex items-center justify-center p-4 ${!hasEventEnded ? 'border-l-2 border-dashed border-dashedBorder' : ''}`}>
            <div className="flex justify-between flex-col gap-4">
              <div className="flex items-end flex-col gap-0">
                <span className="text-xs text-blackie">Total Members</span>
                <span className="text-AttendeeBlue text-xl text-right">
                  {selectedSubscriber
                    ? 1
                    : eventData?.eventSubscriptions?.length}
                </span>
              </div>
              <div className="flex items-end flex-col gap-0">
                <span className="text-xs text-blackie">Parent/Guardian</span>
                <span className="text-AttendeeBlue text-base capitalize text-right">
                  {userData?.profiles?.child?.attendee?.firstName} {userData?.profiles?.child?.attendee?.lastName}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassModal;
